import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AlertStreamService } from '../alerts/alert-stream.service';
import { AuthService } from '../auth/auth.service';
import { injectAppTvMode } from '../../shared/tv-mode';
import { environment } from '../../../environments/environment';

interface BypassStatusResponse {
  active: boolean;
}

/**
 * Estado del bypass de emergencia (RF09), para el cartel persistente del panel. Combina dos
 * fuentes a propósito: un fetch inicial a GET /admin/settings/bypass-status (la verdad al
 * momento de loguearse — necesario porque el stream SSE solo trae eventos ocurridos con el panel
 * abierto, no historial) y el stream de alertas en vivo (BYPASS_ENABLED/BYPASS_DISABLED) para
 * reflejar un toggle sin esperar a recargar. El evento en vivo, si ya llegó alguno esta sesión,
 * siempre gana sobre el fetch inicial (es más fresco).
 */
@Injectable({ providedIn: 'root' })
export class BypassStatusService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  // QA 2026-10: not in TV mode either (an ADMIN previewing ?modo=tv shows nothing that uses it),
  // nor before TV mode is known (first navigation); leaving TV mode turns the token on.
  private readonly tvMode = injectAppTvMode();
  private readonly sessionToken = computed(() =>
    this.tvMode() === false ? this.auth.opsSessionToken() : null,
  );
  private readonly alertStream = inject(AlertStreamService);

  private readonly initialActive = signal<boolean | null>(null);

  private readonly liveCode = computed(() => {
    const bypassAlerts = this.alertStream.alerts().filter((a) => a.source === 'BYPASS');
    // alerts() trae las más nuevas primero (ver AlertStreamService.dispatchFrame).
    return bypassAlerts.length > 0 ? bypassAlerts[0].code : null;
  });

  /** true mientras el bypass de emergencia está activo. */
  readonly active = computed(() => {
    const live = this.liveCode();
    return live !== null ? live === 'BYPASS_ENABLED' : (this.initialActive() ?? false);
  });

  constructor() {
    // Mismo patrón que AlertStreamService: reacciona al token (no solo al booleano de sesión),
    // así un re-login vuelve a pedir el estado inicial con la credencial nueva. Only OPS sessions:
    // bypass-status is OPS tier in CS (a METRICS_VIEWER got a 403 on every login).
    effect(() => {
      const token = this.sessionToken();
      untracked(() => {
        this.initialActive.set(null);
        if (token !== null) {
          this.fetchInitial();
        }
      });
    });
  }

  private fetchInitial(): void {
    this.http.get<BypassStatusResponse>(`${environment.apiUrl}/admin/settings/bypass-status`).subscribe({
      next: (response) => this.initialActive.set(response.active),
      // Sin acceso (rol sin permiso) o backend caído: no rompe el panel, el cartel simplemente
      // no se muestra hasta que el stream traiga un evento en vivo.
      error: () => this.initialActive.set(false),
    });
  }
}
